import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import BranchLink from '@/components/BranchLink';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, ArrowRight, Check, X, PackageCheck, Trash2, ArrowLeftRight, Clock, Truck, ClipboardCheck, CalendarPlus, Send, Printer, FileText, Search, Download, FileSpreadsheet } from 'lucide-react';
import { BranchInvoicesTab } from '@/components/transfers/BranchInvoicesTab';
import api from '@/lib/api';
import { exportTableToPDF } from '@/lib/pdfExport';
import { exportToCsv } from '@/lib/exportCsv';
import { printBranchOrderSlip } from '@/lib/printBranchOrder';
import { useBranchStore } from '@/stores';
import { BranchFilter } from '@/components/filters/BranchFilter';
import { SearchableSelect } from '@/components/SearchableSelect';
import { StockRequestButton } from '@/components/stock/StockRequestButton';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

const ITEM_SEARCH_KEYS = ['name', 'sku', 'code', 'item_code', 'barcode', 'description'];

const STATUS_STYLES = {
  pending: 'bg-warning/20 text-warning border-warning/30',
  approved: 'bg-info/20 text-info border-info/30',
  in_transit: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  completed: 'bg-success/20 text-success border-success/30',
  rejected: 'bg-error/20 text-error border-error/30',
  cancelled: 'bg-muted text-muted-foreground border-border',
};

export default function TransfersPage() {
  const { t } = useLanguage();
  const [transfers, setTransfers] = useState([]);
  const { branches, fetchBranches: _fetchBr } = useBranchStore();
  const [items, setItems] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [receiveDialog, setReceiveDialog] = useState(null);
  const [receivedItems, setReceivedItems] = useState([]);
  const [receiverNotes, setReceiverNotes] = useState('');
  const [demands, setDemands] = useState([]);
  const [showDemand, setShowDemand] = useState(false);
  const [demandForm, setDemandForm] = useState({ branch_id: '', demand_date: '', items: [{ item_id: '', quantity: '' }], notes: '' });
  const [form, setForm] = useState({ from_branch_id: '', to_branch_id: '', reason: '', notes: '', items: [{ item_id: '', quantity: '', kind: 'item' }] });
  // iter880 — Branch filter for viewing transfers (matches from_branch_id OR to_branch_id).
  const [viewBranchId, setViewBranchId] = useState(null);
  // iter — search + date-range filters (apply across all tabs); status is the tab.
  const [searchQ, setSearchQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // iter937 — current user's branch (requester default for Branch Stock Request).
  const myBranchId = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}')?.branch_id || null; } catch { return null; }
  })();

  const fetchAll = async () => {
    try {
      const params = {};
      if (viewBranchId) params.branch_id = viewBranchId;  // backend may filter; frontend also filters below
      const [tR, , iR, dR] = await Promise.all([
        api.get('/stock-transfers', { params }), Promise.resolve({ data: [] }), api.get('/items'),
        api.get('/branch-demands').catch(() => ({ data: [] })),
      ]);
      setTransfers(tR.data); setItems(iR.data); setDemands(dR.data || []);
      api.get('/cashier/menu-all').then(r => setMenuItems(r.data || [])).catch(() => {});
    } catch { /* silent */ }
  };

  useEffect(() => { fetchAll(); }, [viewBranchId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Client-side branch filter (fallback if backend doesn't respect the param).
  const branchMatch = (t) => !viewBranchId || t.from_branch_id === viewBranchId || t.to_branch_id === viewBranchId;
  // iter — search (transfer #, item name, branch names) + date-range filters.
  const _txnDate = (t) => String(t.requested_at || t.created_at || '').slice(0, 10);
  const textMatch = (t) => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return true;
    const hay = [t.request_number, t.from_branch_name, t.to_branch_name,
      ...((t.items || []).map(i => i.item_name))].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
  };
  const dateMatch = (t) => {
    const d = _txnDate(t);
    if (dateFrom && d && d < dateFrom) return false;
    if (dateTo && d && d > dateTo) return false;
    return true;
  };
  const filterMatch = (t) => branchMatch(t) && textMatch(t) && dateMatch(t);
  const pending = transfers.filter(t => t.status === 'pending' && filterMatch(t));
  const approved = transfers.filter(t => t.status === 'approved' && filterMatch(t));
  const inTransit = transfers.filter(t => t.status === 'in_transit' && filterMatch(t));
  const active = [...approved, ...inTransit];
  const history = transfers.filter(t => ['completed', 'rejected', 'cancelled'].includes(t.status) && filterMatch(t));
  const hasFilters = !!(searchQ.trim() || dateFrom || dateTo || viewBranchId);

  // iter — export the currently-filtered transfers (across all statuses) with
  // item-level detail. Reuses the shared exportTableToPDF + exportToCsv utils.
  const _exportRows = () => transfers.filter(filterMatch).sort((a, b) => _txnDate(b).localeCompare(_txnDate(a)));
  const _itemsText = (t) => (t.items || []).map(i => `${i.item_name} ×${i.quantity}${i.unit ? ' ' + i.unit : ''}`).join('; ') || '—';
  const exportTransfersPDF = () => {
    const rows = _exportRows().map(t => [
      t.request_number || String(t.id || '').slice(0, 8) || '—',
      _txnDate(t) || '—',
      t.from_branch_name || 'N/A',
      t.to_branch_name || 'N/A',
      t.status === 'in_transit' ? 'In Transit' : (t.status || '—'),
      _itemsText(t),
      t.requested_by_name || '—',
      t.reviewed_by_name || '—',
    ]);
    if (!rows.length) { toast.info('No transfers match the current filters'); return; }
    exportTableToPDF('Stock Transfers', `${rows.length} transfer(s)${hasFilters ? ' · filtered' : ''}`,
      ['Transfer #', 'Date', 'From', 'To', 'Status', 'Items', 'Requested By', 'Reviewed By'], rows,
      { landscape: true, columnStyles: { columnStyles: { 5: { cellWidth: 70 } } } });
  };
  const exportTransfersExcel = () => {
    const rows = _exportRows().map(t => ({
      transfer_no: t.request_number || t.id,
      date: _txnDate(t),
      from_branch: t.from_branch_name || 'N/A',
      to_branch: t.to_branch_name || 'N/A',
      status: t.status,
      items: _itemsText(t),
      item_count: (t.items || []).length,
      requested_by: t.requested_by_name || '',
      reviewed_by: t.reviewed_by_name || '',
    }));
    if (!rows.length) { toast.info('No transfers match the current filters'); return; }
    exportToCsv(`stock_transfers_${new Date().toISOString().slice(0, 10)}.csv`, rows, [
      { key: 'transfer_no', label: 'Transfer #' }, { key: 'date', label: 'Date' },
      { key: 'from_branch', label: 'From' }, { key: 'to_branch', label: 'To' },
      { key: 'status', label: 'Status' }, { key: 'items', label: 'Items' },
      { key: 'item_count', label: 'Item Count' }, { key: 'requested_by', label: 'Requested By' },
      { key: 'reviewed_by', label: 'Reviewed By' },
    ]);
    toast.success(`Exported ${rows.length} transfer(s) to CSV`);
  };

  const createTransfer = async () => {
    const validItems = form.items.filter(i => i.item_id && parseFloat(i.quantity) > 0);
    if (!form.from_branch_id || !form.to_branch_id) { toast.error('Select both branches'); return; }
    if (validItems.length === 0) { toast.error('Add at least one item'); return; }
    try {
      await api.post('/stock-transfers', { ...form, items: validItems.map(i => ({ item_id: i.item_id, quantity: parseFloat(i.quantity), kind: i.kind || 'item' })) });
      toast.success('Transfer request created');
      setShowCreate(false);
      setForm({ from_branch_id: '', to_branch_id: '', reason: '', notes: '', items: [{ item_id: '', quantity: '', kind: 'item' }] });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const reorderTransfer = async (id) => {
    try {
      await api.post(`/stock-transfers/${id}/reorder`, {});
      toast.success('Re-order request created');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reorder'); }
  };

  // Reuse the shared internal (non-tax) branch-order slip printer. From/To
  // follow the physical transfer record (from = source, to = destination).
  const printTransferSlip = (tr) => {
    printBranchOrderSlip({
      requestNumber: tr.request_number,
      fromBranch: tr.from_branch_name,
      toBranch: tr.to_branch_name,
      dateTime: tr.requested_at,
      status: tr.status,
      items: (tr.items || []).map((i) => ({ name: i.item_name, qty: i.quantity, unit: i.unit })),
      notes: tr.reason || tr.notes,
    });
  };

  const openReceiveDialog = (t) => {
    setReceiveDialog(t);
    setReceivedItems((t.items || []).map(i => ({ ...i, received_qty: i.quantity, notes: '' })));
    setReceiverNotes('');
  };

  const completeWithConfirmation = async () => {
    if (!receiveDialog) return;
    try {
      await api.put(`/stock-transfers/${receiveDialog.id}/complete`, {
        received_items: receivedItems.map(i => ({ item_id: i.item_id, item_name: i.item_name || i.name, kind: i.kind, received_qty: parseFloat(i.received_qty) || 0, unit: i.unit })),
        receiver_notes: receiverNotes,
      });
      toast.success('Transfer completed with confirmed quantities');
      setReceiveDialog(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const submitDemand = async () => {
    const validItems = demandForm.items.filter(i => i.item_id && parseFloat(i.quantity) > 0);
    if (!demandForm.branch_id) { toast.error('Select branch'); return; }
    if (!validItems.length) { toast.error('Add at least one item'); return; }
    try {
      await api.post('/branch-demands', { ...demandForm, items: validItems.map(i => ({ item_id: i.item_id, quantity: parseFloat(i.quantity) })) });
      toast.success('Next-day demand submitted');
      setShowDemand(false);
      setDemandForm({ branch_id: '', demand_date: '', items: [{ item_id: '', quantity: '' }], notes: '' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const convertDemandToTransfer = async (demandId, fromBranch) => {
    try {
      await api.put(`/branch-demands/${demandId}/convert`, { from_branch_id: fromBranch });
      toast.success('Demand converted to transfer');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const TransferCard = ({ t: tr, showActions }) => (
    <Card className="border-border hover:shadow-sm transition-shadow" data-testid={`transfer-card-${tr.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge className={`text-xs border ${STATUS_STYLES[tr.status]}`}>{tr.status === 'in_transit' ? 'In Transit' : tr.status}</Badge>
            {tr.request_number && <span className="text-[10px] font-mono text-muted-foreground" data-testid={`transfer-reqno-${tr.id}`}>{tr.request_number}</span>}
            <span className="text-xs text-muted-foreground">{tr.requested_at ? new Date(tr.requested_at).toLocaleDateString('en-GB') : ''}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">by {tr.requested_by_name}</span>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => printTransferSlip(tr)} data-testid={`print-order-btn-${tr.id}`} title="Print / Reprint order slip">
              <Printer size={13} className="mr-1" /> Print
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1 text-center p-2 bg-muted/50 rounded-lg">
            <p className="text-[10px] text-muted-foreground uppercase">From</p>
            <p className="text-sm font-bold">
              {tr.from_branch_id ? <BranchLink branchId={tr.from_branch_id} name={tr.from_branch_name} /> : (tr.from_branch_name || <span className="text-muted-foreground font-normal italic">N/A</span>)}
            </p>
          </div>
          <ArrowRight size={18} className="text-muted-foreground flex-shrink-0" />
          <div className="flex-1 text-center p-2 bg-muted/50 rounded-lg">
            <p className="text-[10px] text-muted-foreground uppercase">To</p>
            <p className="text-sm font-bold">
              {tr.to_branch_id ? <BranchLink branchId={tr.to_branch_id} name={tr.to_branch_name} /> : (tr.to_branch_name || <span className="text-muted-foreground font-normal italic">N/A</span>)}
            </p>
          </div>
        </div>
        <div className="space-y-1 mb-3">
          {(tr.items || []).map((item, i) => (
            <div key={i} className="flex justify-between text-xs bg-muted/50 px-2 py-1.5 rounded">
              <span className="font-medium flex items-center gap-1.5">
                {item.item_name}
                {item.kind === 'menu_item' && <Badge className="text-[9px] px-1 py-0 bg-orange-100 text-orange-700 border-orange-200">Menu</Badge>}
              </span>
              <span className="font-mono">{item.quantity} {item.unit || 'pc'}</span>
            </div>
          ))}
        </div>
        {tr.reorder_of && <p className="text-[10px] text-indigo-600 mb-1" data-testid={`transfer-reorder-of-${tr.id}`}>↻ Re-order of a previous request</p>}
        {tr.reason && <p className="text-xs text-muted-foreground mb-2">Reason: {tr.reason}</p>}
        {tr.rejection_reason && <p className="text-xs text-error mb-2">Rejected: {tr.rejection_reason}</p>}
        {tr.receiver_notes && <p className="text-xs text-success mb-2">Receiver notes: {tr.receiver_notes}</p>}
        {tr.reviewed_by_name && <p className="text-[10px] text-muted-foreground">Reviewed by: {tr.reviewed_by_name}</p>}
        {tr.status === 'cancelled' && <p className="text-[10px] text-muted-foreground" data-testid={`transfer-cancelled-by-${tr.id}`}>Cancelled by: {tr.cancelled_by_name || '—'}{tr.cancelled_at ? ` · ${new Date(tr.cancelled_at).toLocaleString()}` : ''}</p>}
        {['completed', 'rejected', 'cancelled'].includes(tr.status) && (
          <div className="mt-3 pt-3 border-t">
            <Button size="sm" variant="outline" className="rounded-xl text-xs h-8" data-testid={`reorder-btn-${tr.id}`}
              onClick={() => reorderTransfer(tr.id)}>
              <ArrowLeftRight size={13} className="mr-1" /> Re-order
            </Button>
          </div>
        )}
        {showActions && (
          <div className="flex gap-2 mt-3 pt-3 border-t">
            {tr.status === 'pending' && (
              <>
                <Button size="sm" className="flex-1 rounded-xl bg-success hover:bg-success" data-testid="approve-btn"
                  onClick={async () => { try { await api.put(`/stock-transfers/${tr.id}/approve`); toast.success('Approved'); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } }}>
                  <Check size={14} className="mr-1" />Approve
                </Button>
                <Button size="sm" variant="outline" className="flex-1 rounded-xl border-error/30 text-error hover:bg-error/10" data-testid="reject-btn"
                  onClick={() => { setRejectDialog(tr); setRejectReason(''); }}>
                  <X size={14} className="mr-1" />Reject
                </Button>
              </>
            )}
            {tr.status === 'approved' && (
              <>
                <Button size="sm" className="flex-1 rounded-xl bg-indigo-500 hover:bg-indigo-600" data-testid="dispatch-btn"
                  onClick={async () => { try { await api.put(`/stock-transfers/${tr.id}/dispatch`); toast.success('Marked as dispatched'); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } }}>
                  <Truck size={14} className="mr-1" />Dispatch
                </Button>
                <Button size="sm" className="flex-1 rounded-xl bg-success hover:bg-success" data-testid="receive-btn"
                  onClick={() => openReceiveDialog(tr)}>
                  <ClipboardCheck size={14} className="mr-1" />Receive & Confirm
                </Button>
              </>
            )}
            {tr.status === 'in_transit' && (
              <Button size="sm" className="flex-1 rounded-xl bg-success hover:bg-success" data-testid="receive-btn-2"
                onClick={() => openReceiveDialog(tr)}>
                <ClipboardCheck size={14} className="mr-1" />Receive & Confirm
              </Button>
            )}
            {tr.status === 'pending' && (
              <Button size="sm" variant="ghost" className="h-8 text-xs text-error" data-testid={`delete-transfer-btn-${tr.id}`}
                onClick={async () => { if (window.confirm('Cancel this pending request? It will be kept in history as Cancelled.')) { try { await api.delete(`/stock-transfers/${tr.id}`); fetchAll(); toast.success('Request cancelled'); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); } } }}>
                <Trash2 size={12} />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="transfers-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-lg shadow-orange-200/50">
              <ArrowLeftRight size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="transfers-title">Stock Transfers</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Move stock/goods between branches with receiver confirmation</p>
              <p className="text-xs text-muted-foreground mt-1">
                Moving cash or bank funds?{' '}
                <Link to="/cash-transfers" className="text-primary font-medium hover:underline" data-testid="goto-money-transfers-link">Go to Money Transfers →</Link>
              </p>
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            {/* iter880 — Branch filter for viewing inbound + outbound transfers. */}
            <BranchFilter value={viewBranchId} onChange={setViewBranchId} width="w-36" testid="transfers-branch" />
            <StockRequestButton myBranchId={myBranchId} size="sm" variant="outline" label="Branch Stock Request" className="rounded-full h-9 text-xs" />
            <Button variant="outline" onClick={() => setShowDemand(true)} className="rounded-full h-9 text-xs" data-testid="new-demand-btn">
              <CalendarPlus size={14} className="mr-1" /> Next-Day Demand
            </Button>
            <Button className="bg-orange-500 hover:bg-orange-600 rounded-full h-9 text-xs" onClick={() => setShowCreate(true)} data-testid="new-transfer-btn">
              <Plus size={14} className="mr-1" /> New Transfer
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Card className="border-warning/30"><CardContent className="p-3 text-center"><p className="text-[10px] text-warning font-medium">Pending</p><p className="text-xl font-bold font-outfit text-warning">{pending.length}</p></CardContent></Card>
          <Card className="border-info/30"><CardContent className="p-3 text-center"><p className="text-[10px] text-info font-medium">Approved</p><p className="text-xl font-bold font-outfit text-info">{approved.length}</p></CardContent></Card>
          <Card className="border-indigo-100"><CardContent className="p-3 text-center"><p className="text-[10px] text-indigo-600 font-medium">In Transit</p><p className="text-xl font-bold font-outfit text-indigo-700">{inTransit.length}</p></CardContent></Card>
          <Card className="border-success/30"><CardContent className="p-3 text-center"><p className="text-[10px] text-success font-medium">Completed</p><p className="text-xl font-bold font-outfit text-success">{transfers.filter(t => t.status === 'completed').length}</p></CardContent></Card>
          <Card className="border-purple-100"><CardContent className="p-3 text-center"><p className="text-[10px] text-purple-600 font-medium">Demands</p><p className="text-xl font-bold font-outfit text-purple-700">{demands.filter(d => d.status === 'pending').length}</p></CardContent></Card>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center gap-2 bg-muted/30 border border-border rounded-xl p-2" data-testid="transfers-filter-toolbar">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Search transfer #, item or branch…"
              className="h-9 pl-8 text-xs"
              data-testid="transfers-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Label className="text-[10px] text-muted-foreground whitespace-nowrap">From</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 text-xs w-[140px]" data-testid="transfers-date-from" />
            </div>
            <div className="flex items-center gap-1">
              <Label className="text-[10px] text-muted-foreground whitespace-nowrap">To</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 text-xs w-[140px]" data-testid="transfers-date-to" />
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setSearchQ(''); setDateFrom(''); setDateTo(''); setViewBranchId(null); }} data-testid="transfers-clear-filters">
                <X size={13} className="mr-1" /> Clear
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 lg:ml-auto">
            <Button variant="outline" size="sm" className="h-9 text-xs rounded-lg" onClick={exportTransfersPDF} data-testid="transfers-export-pdf">
              <Download size={13} className="mr-1" /> PDF
            </Button>
            <Button variant="outline" size="sm" className="h-9 text-xs rounded-lg" onClick={exportTransfersExcel} data-testid="transfers-export-excel">
              <FileSpreadsheet size={13} className="mr-1" /> Excel
            </Button>
          </div>
        </div>

        <Tabs defaultValue="pending">
          <TabsList className="flex-wrap">
            <TabsTrigger value="pending"><Clock size={12} className="mr-1" />Pending ({pending.length})</TabsTrigger>
            <TabsTrigger value="active"><Truck size={12} className="mr-1" />Active ({active.length})</TabsTrigger>
            <TabsTrigger value="demands"><CalendarPlus size={12} className="mr-1" />Demands ({demands.length})</TabsTrigger>
            <TabsTrigger value="history">History ({history.length})</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="transfers-invoices-tab"><FileText size={12} className="mr-1" />Invoices</TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="pending-transfers">
              {pending.length === 0 && <p className="text-sm text-muted-foreground col-span-full text-center py-8">No pending requests</p>}
              {pending.map(t => <TransferCard key={t.id} t={t} showActions />)}
            </div>
          </TabsContent>

          <TabsContent value="active">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="active-transfers">
              {active.length === 0 && <p className="text-sm text-muted-foreground col-span-full text-center py-8">No active transfers</p>}
              {active.map(t => <TransferCard key={t.id} t={t} showActions />)}
            </div>
          </TabsContent>

          <TabsContent value="demands">
            <div className="space-y-3" data-testid="demands-list">
              {demands.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No demands yet. Branches can submit next-day requirements.</p>}
              {demands.map(d => (
                <Card key={d.id} data-testid={`demand-${d.id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium text-sm">{d.branch_name} <span className="text-muted-foreground font-normal">— {d.demand_date}</span></p>
                        <p className="text-xs text-muted-foreground">by {d.created_by_name} | {new Date(d.created_at).toLocaleString()}</p>
                      </div>
                      <Badge className={d.status === 'pending' ? 'bg-warning/20 text-warning' : d.status === 'converted' ? 'bg-success/20 text-success' : 'bg-muted text-muted-foreground'} >{d.status}</Badge>
                    </div>
                    <div className="space-y-1 mb-2">
                      {(d.items || []).map((item, i) => (
                        <div key={i} className="flex justify-between text-xs bg-muted/50 px-2 py-1 rounded">
                          <span>{item.item_name}</span><span className="font-mono">{item.quantity} {item.unit || 'pc'}</span>
                        </div>
                      ))}
                    </div>
                    {d.notes && <p className="text-xs text-muted-foreground mb-2">{d.notes}</p>}
                    {d.status === 'pending' && (
                      <div className="flex gap-2 mt-2">
                        <Select onValueChange={v => convertDemandToTransfer(d.id, v)}>
                          <SelectTrigger className="flex-1 h-8 text-xs" data-testid={`convert-demand-${d.id}`}><SelectValue placeholder="Convert to transfer from..." /></SelectTrigger>
                          <SelectContent>
                            {branches.filter(b => b.id !== d.branch_id).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="history">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="transfer-history">
              {history.length === 0 && <p className="text-sm text-muted-foreground col-span-full text-center py-8">No transfer history</p>}
              {history.map(t => <TransferCard key={t.id} t={t} showActions={false} />)}
            </div>
          </TabsContent>
          <TabsContent value="invoices">
            <BranchInvoicesTab branches={branches} />
          </TabsContent>
        </Tabs>

        {/* Create Transfer Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-lg" data-testid="create-transfer-dialog">
            <DialogHeader><DialogTitle className="font-outfit">New Transfer Request</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Source Branch (from)</Label>
                  <Select value={form.from_branch_id} onValueChange={v => setForm({ ...form, from_branch_id: v })}>
                    <SelectTrigger className="h-9" data-testid="from-branch"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Destination Branch (to)</Label>
                  <Select value={form.to_branch_id} onValueChange={v => setForm({ ...form, to_branch_id: v })}>
                    <SelectTrigger className="h-9" data-testid="to-branch"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>{branches.filter(b => b.id !== form.from_branch_id).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Items</Label>
                <div className="space-y-2">
                  {form.items.map((item, i) => (
                    <div key={i} className="flex gap-2 items-end">
                      <Select value={item.kind || 'item'} onValueChange={v => { const u = [...form.items]; u[i].kind = v; u[i].item_id = ''; setForm({ ...form, items: u }); }}>
                        <SelectTrigger className="w-28 h-9 text-xs shrink-0" data-testid={`item-kind-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="item">Inventory</SelectItem>
                          <SelectItem value="menu_item">Menu Item</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex-1">
                        <SearchableSelect
                          items={(item.kind === 'menu_item') ? menuItems : items}
                          value={item.item_id}
                          onChange={v => { const u = [...form.items]; u[i].item_id = v; setForm({ ...form, items: u }); }}
                          searchKeys={ITEM_SEARCH_KEYS}
                          placeholder={item.kind === 'menu_item' ? 'Search menu item…' : 'Search name / code / SKU…'}
                          renderItem={(it) => (
                            <div className="flex items-center justify-between w-full gap-2">
                              <span className="truncate">{it.name}</span>
                              <span className="text-[10px] text-muted-foreground font-mono shrink-0">{item.kind === 'menu_item' ? `SAR ${it.price}` : ((it.sku || it.code || it.item_code || it.barcode) || (it.unit || 'pc'))}</span>
                            </div>
                          )}
                          data-testid={`item-select-${i}`}
                        />
                      </div>
                      <Input type="number" placeholder="Qty" value={item.quantity} onChange={e => { const u = [...form.items]; u[i].quantity = e.target.value; setForm({ ...form, items: u }); }} className="w-20 h-9 text-xs" data-testid={`item-qty-${i}`} />
                      {form.items.length > 1 && <Button size="sm" variant="ghost" className="h-9 px-2" onClick={() => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) })}><X size={14} /></Button>}
                    </div>
                  ))}
                  <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => setForm({ ...form, items: [...form.items, { item_id: '', quantity: '', kind: 'item' }] })}><Plus size={12} className="mr-1" />Add Item</Button>
                </div>
              </div>
              <div><Label className="text-xs">Reason</Label><Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} className="h-9" data-testid="transfer-reason" /></div>
              <Button className="w-full rounded-xl bg-orange-500 hover:bg-orange-600" onClick={createTransfer} data-testid="submit-transfer-btn"><ArrowLeftRight size={14} className="mr-1" />Submit Transfer</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Receiver Confirmation Dialog */}
        <Dialog open={!!receiveDialog} onOpenChange={v => !v && setReceiveDialog(null)}>
          <DialogContent className="max-w-lg" data-testid="receive-dialog">
            <DialogHeader><DialogTitle className="font-outfit flex items-center gap-2"><ClipboardCheck size={18} className="text-success" /> Confirm Receipt</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground">Review items and adjust quantities if they differ from what was sent.</p>
            <div className="space-y-2 mt-2">
              {receivedItems.map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg" data-testid={`receive-item-${i}`}>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{item.item_name}</p>
                    <p className="text-[10px] text-muted-foreground">Sent: {item.quantity} {item.unit || 'pc'}</p>
                  </div>
                  <div className="w-24">
                    <Label className="text-[10px]">Received</Label>
                    <Input type="number" value={item.received_qty} onChange={e => { const u = [...receivedItems]; u[i].received_qty = e.target.value; setReceivedItems(u); }} className="h-8 text-xs" data-testid={`received-qty-${i}`} />
                  </div>
                </div>
              ))}
            </div>
            <div><Label className="text-xs">Receiver Notes</Label><Textarea value={receiverNotes} onChange={e => setReceiverNotes(e.target.value)} placeholder="Any discrepancies or notes..." className="h-16 mt-1" data-testid="receiver-notes" /></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReceiveDialog(null)}>Cancel</Button>
              <Button className="bg-success hover:bg-success" onClick={completeWithConfirmation} data-testid="confirm-receive-btn"><PackageCheck size={14} className="mr-1" />Confirm & Complete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Next-Day Demand Dialog */}
        <Dialog open={showDemand} onOpenChange={setShowDemand}>
          <DialogContent className="max-w-lg" data-testid="demand-dialog">
            <DialogHeader><DialogTitle className="font-outfit flex items-center gap-2"><CalendarPlus size={18} className="text-purple-600" /> Next-Day Demand</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground mb-2">Branch requests items needed for tomorrow.</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Branch *</Label>
                  <Select value={demandForm.branch_id || 'none'} onValueChange={v => setDemandForm({ ...demandForm, branch_id: v === 'none' ? '' : v })}>
                    <SelectTrigger className="h-9" data-testid="demand-branch"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent><SelectItem value="none" disabled>Select...</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Date</Label><Input type="date" value={demandForm.demand_date} onChange={e => setDemandForm({ ...demandForm, demand_date: e.target.value })} className="h-9" data-testid="demand-date" /></div>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Items Needed</Label>
                {demandForm.items.map((item, i) => (
                  <div key={i} className="flex gap-2 items-end mb-2">
                    <div className="flex-1">
                      <SearchableSelect
                        items={items}
                        value={item.item_id}
                        onChange={v => { const u = [...demandForm.items]; u[i].item_id = v; setDemandForm({ ...demandForm, items: u }); }}
                        searchKeys={ITEM_SEARCH_KEYS}
                        placeholder="Search name / code / SKU…"
                        renderItem={(it) => (
                          <div className="flex items-center justify-between w-full gap-2">
                            <span className="truncate">{it.name}</span>
                            <span className="text-[10px] text-muted-foreground font-mono shrink-0">{(it.sku || it.code || it.item_code || it.barcode) || (it.unit || 'pc')}</span>
                          </div>
                        )}
                        data-testid={`demand-item-select-${i}`}
                      />
                    </div>
                    <Input type="number" placeholder="Qty" value={item.quantity} onChange={e => { const u = [...demandForm.items]; u[i].quantity = e.target.value; setDemandForm({ ...demandForm, items: u }); }} className="w-24 h-9 text-xs" />
                    {demandForm.items.length > 1 && <Button size="sm" variant="ghost" className="h-9 px-2" onClick={() => setDemandForm({ ...demandForm, items: demandForm.items.filter((_, idx) => idx !== i) })}><X size={14} /></Button>}
                  </div>
                ))}
                <Button size="sm" variant="outline" className="text-xs" onClick={() => setDemandForm({ ...demandForm, items: [...demandForm.items, { item_id: '', quantity: '' }] })}><Plus size={12} className="mr-1" />Add Item</Button>
              </div>
              <div><Label className="text-xs">Notes</Label><Textarea value={demandForm.notes} onChange={e => setDemandForm({ ...demandForm, notes: e.target.value })} className="h-16" data-testid="demand-notes" /></div>
              <Button className="w-full rounded-xl bg-purple-500 hover:bg-purple-600" onClick={submitDemand} data-testid="submit-demand-btn"><Send size={14} className="mr-1" />Submit Demand</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={!!rejectDialog} onOpenChange={v => !v && setRejectDialog(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="font-outfit text-base">Reject Transfer</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Reason for rejection</Label>
                <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Why?" className="h-20 text-sm" data-testid="reject-reason" /></div>
              <Button className="w-full rounded-xl bg-error hover:bg-error" data-testid="confirm-reject-btn" onClick={async () => {
                try { await api.put(`/stock-transfers/${rejectDialog.id}/reject`, { reason: rejectReason }); toast.success('Rejected'); setRejectDialog(null); fetchAll(); }
                catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
              }}>Reject Transfer</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
